import { Renderer } from "../pipeline/renderer"
import { Texture } from "../backends/webgl/texture"
import { mat4 } from "gl-matrix"
import { Rectangle } from "../types"
import { TextureArray } from "../backends/webgl/texture-array"

export const copyFrom = (options: {
	renderer: Renderer
	source: Texture
	srcLocation: Rectangle
	destination: Texture
}) => {
	const { renderer, source, destination, srcLocation } = options
	const { gl } = renderer.backend

	source.bindReadFramebuffer()
	destination.bindDrawFramebuffer()

	const { x, y, width, height } = srcLocation
	const sw = source.width
	const sh = source.height
	gl.blitFramebuffer(
		x * sw,
		y * sh,
		(x + width) * sw,
		(y + height) * sh, // Source rectangle
		0,
		0,
		destination.width,
		destination.height, // Destination rectangle
		gl.COLOR_BUFFER_BIT, // Copy the color buffer
		gl.LINEAR, // Interpolation
	)

	// Unbind framebuffers if necessary
	gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null)
	gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null)
}

export const copyFromWithTransform = (options: {
	renderer: Renderer
	source: Texture
	srcLocation: Rectangle
	destination: Texture
	srcMatrix?: mat4
}) => {
	const { renderer, source, destination, srcLocation } = options
	const { gl, shaders } = renderer.backend

	renderer.setRenderTarget(destination)
	gl.clearColor(0, 0, 0, 1)
	gl.clear(gl.COLOR_BUFFER_BIT)
	const [offsetX, offsetY] = [
		1 - (srcLocation.x + srcLocation.width * 0.5) * 2,
		1 - (srcLocation.y + srcLocation.height * 0.5) * 2,
	]
	const [scaleX, scaleY] = [1.0 / srcLocation.width, 1.0 / srcLocation.height]
	const matOffset = mat4.create()
	mat4.fromTranslation(matOffset, [offsetX, offsetY, 0.0])
	const matScale = mat4.create()
	mat4.fromScaling(matScale, [scaleX, scaleY, 1.0])
	let mat = mat4.create()
	mat4.multiply(mat, matScale, matOffset)
	if (options.srcMatrix) {
		const tmp = mat4.create()
		mat4.multiply(tmp, mat, options.srcMatrix)
		mat = tmp
	}

	source.bind(0)
	shaders.viewport.render({
		inputTexture: 0,
		viewMatrix: mat,
		filterNearest: false,
	})
}

// With alpha blending and tranformMatrix, not doable by blitFramebuffer
export const copyTo = (options: {
	renderer: Renderer
	source: Texture
	destination: Texture
	destLocation: Rectangle
	mask: Texture
	alphaDestination?: Texture
	dstMatrix?: mat4
}) => {
	const { renderer, source, destination, destLocation, mask, alphaDestination } = options
	const { shaders, gl } = renderer.backend

	renderer.setRenderTarget(destination, alphaDestination ? [alphaDestination] : undefined)

	// Enable alpha blending
	gl.enable(gl.BLEND)
	gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)

	const [offsetX, offsetY] = [
		(destLocation.x + destLocation.width * 0.5) * 2 - 1,
		(destLocation.y + destLocation.height * 0.5) * 2 - 1,
	]
	const [scaleX, scaleY] = [destLocation.width, destLocation.height]
	const matOffset = mat4.create()
	mat4.fromTranslation(matOffset, [offsetX, offsetY, 0.0])
	const matScale = mat4.create()
	mat4.fromScaling(matScale, [scaleX, scaleY, 1.0])
	let mat = mat4.create()
	mat4.multiply(mat, matOffset, matScale)
	if (options.dstMatrix) {
		const tmp = mat4.create()
		mat4.multiply(tmp, options.dstMatrix, mat)
		mat = tmp
	}

	source.bind(0)
	mask.bind(1)
	shaders.healingCombine.render({
		result: 0,
		mask: 1,
		viewMatrix: mat,
	})

	gl.disable(gl.BLEND)
}

export const copyToTextureArray = (options: {
	renderer: Renderer
	source: Texture
	destination: TextureArray
	layerIndex: number
}) => {
	const { renderer, source, destination, layerIndex } = options
	const { gl } = renderer.backend

	source.bindReadFramebuffer()
	destination.bindDrawFramebuffer(layerIndex)

	gl.blitFramebuffer(
		0,
		0,
		source.width,
		source.height, // Source rectangle
		0,
		0,
		destination.width,
		destination.height, // Destination rectangle
		gl.COLOR_BUFFER_BIT, // Copy the color buffer
		gl.NEAREST, // Interpolation
	)

	// Unbind framebuffers if necessary
	gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null)
	gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null)
}
